import "./OcipError.css";
import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Button, Table, Modal } from "react-bootstrap";
import { getIvDataIndex } from "../../utils/ivErrorDataUtils";

/**
 * Spaceone 알람 코드 표 (사용자가 올린 사진 기반)
 * - key: 에러번호(정수)
 * - desc: 내용(원인/상태)
 * - remark: 비고(자동복귀 조건 등)
 *
 * ※ 사진이 살짝 흐린 부분이 있어서, 문구는 보이는 범위에서 최대한 그대로 옮겼어.
 *   만약 실제 매뉴얼 텍스트가 있으면 100% 정확하게 다듬어줄 수 있음.
 */
const SPACEONE_ALARM_CODES = {
  1: { desc: "계통전기 입력전압 50%이하 상태", remark: "5분 후 자동복귀" },
  2: { desc: "계통전기 입력전압 80%이하 상태", remark: "5분 후 자동복귀" },
  3: { desc: "계통전기 입력전압 110%이상 상태", remark: "5분 후 자동복귀" },
  4: { desc: "계통전기 입력전압 130%이상 상태", remark: "5분 후 자동복귀" },
  5: { desc: "계통전기 입력전파수 59.3Hz이하 상태", remark: "5분 후 자동복귀" },
  6: { desc: "계통전기 입력전파수 60.5Hz이상 상태", remark: "5분 후 자동복귀" },

  13: { desc: "인버터 출력 단락 또는 개방 상태", remark: "5분 후 자동복귀" },
  14: { desc: "인버터 L1상 단선된 일광 상태", remark: "5분 후 자동복귀" },
  15: { desc: "인버터 L2상 단선된 일광 상태", remark: "5분 후 자동복귀" },
  16: { desc: "인버터 L3상 단선된 일광 상태", remark: "5분 후 자동복귀" },
  17: { desc: "인버터 출력 과부하 상태", remark: "5분 후 자동복귀" },
  18: { desc: "인버터 출력 과부하 상태 (120%)", remark: "5분 후 자동복귀" },
  19: { desc: "인버터 DC 과전류 상태", remark: "5분 후 자동복귀" },
  20: { desc: "인버터 DC 전압 비정상 상태", remark: "5분 후 자동복귀" },
  21: { desc: "인버터 내부 온도 과열 상태", remark: "80℃ 이하 자동복귀" },

  22: { desc: "계통전기 초기 일회 끊어짐 발생", remark: "5분 후 자동복귀" },
  23: { desc: "연속 중 계통전기 단락", remark: "5분 후 자동복귀" },

  28: { desc: "마그네트 컨택터 열림 오류 발생", remark: "5분 후 자동복귀" },

  30: { desc: "입력 상전압 50%이상 편차 발생", remark: "5분 후 자동복귀" },
  31: { desc: "입력 상전류 50%이상 편차 발생", remark: "5분 후 자동복귀" },
  32: { desc: "계통전압 L1,L2,L3 역상연결 오류 발생", remark: "L1,L2,L3 순서변경" },
  33: { desc: "계통 전원회로 오류", remark: "5분 후 자동복귀" },
  34: { desc: "인버터 출력 AC이상 보호회로 작동", remark: "5분 후 자동복귀" },
  35: { desc: "인버터 단독선 보호회로 작동", remark: "5분 후 자동복귀" },
  36: { desc: "인버터 L1상 과전류 발생", remark: "5분 후 자동복귀" },
  37: { desc: "인버터 L2상 과전류 발생", remark: "5분 후 자동복귀" },
  38: { desc: "인버터 L3상 과전류 발생", remark: "5분 후 자동복귀" },
};

const SPACEONE_STATUS_CODES = {
  0: "정지(Stop)",
  1: "운전대기(Standby)",
  3: "운전중(Running)",
};

function safePickPerInverter(value, dataIndex) {
  if (Array.isArray(value)) {
    if (typeof dataIndex === "number" && dataIndex >= 0) return value[dataIndex];
    return undefined;
  }
  return value;
}

function normalizeInt(v) {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

const SpaceoneError = ({ SelInverter }) => {
  const ivData = useSelector((state) => state.ivData || {});

  const [openModal, setOpenModal] = useState(null); // "ALARM" | "STATUS" | null
  const closeModal = () => setOpenModal(null);
  const dataIndex = getIvDataIndex(ivData, SelInverter);

  // ✅ Spaceone: 에러코드(정수 1개), 상태(정수 1개)
  const alarmCode = useMemo(() => {
    // 가능한 케이스:
    // 1) ivData.Error[0] 가 [인버터별 에러코드 배열] 형태
    // 2) ivData.Error[0] 가 그냥 숫자
    // 3) ivData.error_code 같은 다른 키로 올 수도 있어서 fallback도 넣음
    const raw =
      ivData?.Error?.[0] ??
      ivData?.error_code ??
      ivData?.alarm_code ??
      null;

    return normalizeInt(safePickPerInverter(raw, dataIndex));
  }, [ivData, dataIndex]);

  const statusCode = useMemo(() => {
    const raw =
      ivData?.state ??
      ivData?.Status ??
      ivData?.status ??
      null;

    return normalizeInt(safePickPerInverter(raw, dataIndex));
  }, [ivData, dataIndex]);

  const alarmInfo = alarmCode != null ? SPACEONE_ALARM_CODES[alarmCode] : null;
  const statusLabel =
    statusCode != null ? (SPACEONE_STATUS_CODES[statusCode] ?? `알 수 없음 (${statusCode})`) : null;

  const isAlarmAbnormal = alarmCode != null && alarmCode !== 0;
  // status는 문서상 0/1/3만 정의됐고, 그 외는 "이상"으로 볼지 애매하니
  // 일단 "정지(0)"도 빨간불은 안 켜고, "정의 밖"만 빨간불 처리
  const isStatusAbnormal = statusCode != null && !(statusCode in SPACEONE_STATUS_CODES);

  const renderModalContent = () => {
    if (openModal === "ALARM") {
      return (
        <>
          <Modal.Header closeButton>
            <Modal.Title>Spaceone 알람(에러) 코드</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div style={{ marginBottom: 12 }}>
              <div>
                <b>현재 코드:</b>{" "}
                {alarmCode == null ? "N/A" : alarmCode}
              </div>
              <div>
                <b>의미:</b>{" "}
                {alarmCode == null
                  ? "N/A"
                  : alarmInfo
                  ? alarmInfo.desc
                  : "정의되지 않은 코드"}
              </div>
              <div>
                <b>비고:</b>{" "}
                {alarmCode == null
                  ? "N/A"
                  : alarmInfo
                  ? alarmInfo.remark
                  : "-"}
              </div>
            </div>

            <Table striped bordered size="sm">
              <thead>
                <tr>
                  <th style={{ width: 90 }}>코드</th>
                  <th>내용</th>
                  <th style={{ width: 160 }}>비고</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(SPACEONE_ALARM_CODES)
                  .map((k) => Number(k))
                  .sort((a, b) => a - b)
                  .map((code) => {
                    const row = SPACEONE_ALARM_CODES[code];
                    const isActive = alarmCode === code;
                    return (
                      <tr key={code} className={isActive ? "active-row" : ""}>
                        <td>{code.toString().padStart(2, "0")}</td>
                        <td>{row.desc}</td>
                        <td>{row.remark}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </Table>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>
              닫기
            </Button>
          </Modal.Footer>
        </>
      );
    }

    if (openModal === "STATUS") {
      return (
        <>
          <Modal.Header closeButton>
            <Modal.Title>Spaceone 상태(Status) 코드</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div style={{ marginBottom: 12 }}>
              <div>
                <b>현재 코드:</b>{" "}
                {statusCode == null ? "N/A" : statusCode}
              </div>
              <div>
                <b>의미:</b>{" "}
                {statusCode == null ? "N/A" : statusLabel}
              </div>
            </div>

            <Table striped bordered size="sm">
              <thead>
                <tr>
                  <th style={{ width: 90 }}>코드</th>
                  <th>상태</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(SPACEONE_STATUS_CODES)
                  .map((k) => Number(k))
                  .sort((a, b) => a - b)
                  .map((code) => {
                    const isActive = statusCode === code;
                    return (
                      <tr key={code} className={isActive ? "active-row" : ""}>
                        <td>{code}</td>
                        <td>{SPACEONE_STATUS_CODES[code]}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </Table>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>
              닫기
            </Button>
          </Modal.Footer>
        </>
      );
    }

    return null;
  };

  return (
    <div className="status-list-container">
      <div className="status-list">
        {/* 1) 알람 코드 */}
        <div className="status-item">
          <span className="status-value">
            알람 코드: {alarmCode == null ? "N/A" : alarmCode}
            {"  "}
            {alarmCode == null
              ? ""
              : alarmInfo
              ? `(${alarmInfo.desc})`
              : "(정의되지 않은 코드)"}
          </span>
          <button onClick={() => setOpenModal("ALARM")}>
            <span className={isAlarmAbnormal ? "blinking-icon" : ""}>
              {isAlarmAbnormal ? "🔴" : "🟢"}
            </span>
          </button>
        </div>

        {/* 2) 상태 코드 */}
        <div className="status-item">
          <span className="status-value">
            상태(Status): {statusCode == null ? "N/A" : statusCode}
            {"  "}
            {statusCode == null ? "" : `(${statusLabel})`}
          </span>
          <button onClick={() => setOpenModal("STATUS")}>
            <span className={isStatusAbnormal ? "blinking-icon" : ""}>
              {isStatusAbnormal ? "🔴" : "🟢"}
            </span>
          </button>
        </div>
      </div>

      {openModal !== null && (
        <Modal show onHide={closeModal} size="lg">
          {renderModalContent()}
        </Modal>
      )}
    </div>
  );
};

export default SpaceoneError;
